import type { WidgetConfig } from './types';
import {
    STANDARD_CHART_HEIGHT,
    WIDGET_GRID_GAP_PX,
    WIDGET_SPAN_STEPS,
    WIDGET_HEIGHT_STEPS,
    clampGridSpan,
    clampWidgetHeight,
    type ResizeCorner,
    type WidgetGridSize,
} from './types';

const SEARCH_ROW_PAD = 24;

export function chartColSpan(widget: Pick<WidgetConfig, 'gridSpan'>): number {
    return clampGridSpan(widget.gridSpan);
}

export function chartRowSpan(widget: Pick<WidgetConfig, 'widgetHeight'>): number {
    return clampWidgetHeight(widget.widgetHeight);
}

export function snapChartOrigin(gridX: number, gridY: number, width: number, _height: number): { gridX: number; gridY: number } {
    const w = clampGridSpan(width);
    return {
        gridX: Math.min(WIDGET_SPAN_STEPS - w, Math.max(0, Math.round(gridX))),
        gridY: Math.max(0, Math.round(gridY)),
    };
}

/** Snap a chart's origin from how far the pointer moved, not the cell under it. */
export function originFromDragDelta(
    start: { gridX: number; gridY: number },
    startClient: { x: number; y: number },
    client: { x: number; y: number },
    gridWidth: number,
    width: number,
    height: number,
): { gridX: number; gridY: number } {
    const colW = Math.max(1, gridWidth / WIDGET_SPAN_STEPS);
    const rowH = STANDARD_CHART_HEIGHT + WIDGET_GRID_GAP_PX;
    return snapChartOrigin(
        start.gridX + (client.x - startClient.x) / colW,
        start.gridY + (client.y - startClient.y) / rowH,
        width,
        height,
    );
}

function cellKey(col: number, row: number): string {
    return `${col},${row}`;
}

function addOccupied(used: Set<string>, widget: Pick<WidgetConfig, 'gridX' | 'gridY' | 'gridSpan' | 'widgetHeight'>) {
    const x = widget.gridX ?? 0;
    const y = widget.gridY ?? 0;
    const width = chartColSpan(widget);
    const height = chartRowSpan(widget);
    for (let row = 0; row < height; row += 1) {
        for (let col = 0; col < width; col += 1) {
            used.add(cellKey(x + col, y + row));
        }
    }
}

function rectFits(used: Set<string>, gridX: number, gridY: number, width: number, height: number): boolean {
    if (gridX < 0 || gridY < 0 || gridX + width > WIDGET_SPAN_STEPS) return false;
    for (let row = 0; row < height; row += 1) {
        for (let col = 0; col < width; col += 1) {
            if (used.has(cellKey(gridX + col, gridY + row))) return false;
        }
    }
    return true;
}

function firstFreeSlot(used: Set<string>, width: number, height: number, fromRow = 0): { gridX: number; gridY: number } {
    const maxRow = fromRow + SEARCH_ROW_PAD + used.size;
    for (let row = fromRow; row <= maxRow; row += 1) {
        for (let col = 0; col <= WIDGET_SPAN_STEPS - width; col += 1) {
            if (rectFits(used, col, row, width, height)) return { gridX: col, gridY: row };
        }
    }
    return { gridX: 0, gridY: maxRow + 1 };
}

export function hasChartSlot(widget: Pick<WidgetConfig, 'widgetType' | 'gridX' | 'gridY'>): boolean {
    return widget.widgetType === 'keyMetric'
        || (typeof widget.gridX === 'number' && typeof widget.gridY === 'number');
}

export function packChartsFromOrder(charts: WidgetConfig[]): WidgetConfig[] {
    const used = new Set<string>();
    return charts.map((widget) => {
        const width = chartColSpan(widget);
        const height = chartRowSpan(widget);
        const slot = firstFreeSlot(used, width, height, 0);
        const placed = {
            ...widget,
            gridX: slot.gridX,
            gridY: slot.gridY,
            gridSpan: width,
            widgetHeight: height,
        };
        addOccupied(used, placed);
        return placed;
    });
}

/** Assign missing coordinates without moving charts that already have a valid slot. */
export function assignChartSlots(charts: WidgetConfig[]): WidgetConfig[] {
    if (charts.length === 0) return charts;
    const missing = charts.some((widget) => !hasChartSlot(widget));
    if (missing) {
        const used = new Set<string>();
        const placed: WidgetConfig[] = [];
        for (const widget of charts) {
            const width = chartColSpan(widget);
            const height = chartRowSpan(widget);
            if (hasChartSlot(widget) && rectFits(used, widget.gridX ?? 0, widget.gridY ?? 0, width, height)) {
                const next = { ...widget, gridSpan: width, widgetHeight: height };
                addOccupied(used, next);
                placed.push(next);
                continue;
            }
            const slot = firstFreeSlot(used, width, height, 0);
            const next = { ...widget, gridX: slot.gridX, gridY: slot.gridY, gridSpan: width, widgetHeight: height };
            addOccupied(used, next);
            placed.push(next);
        }
        return placed;
    }

    const used = new Set<string>();
    let overlap = false;
    for (const widget of charts) {
        const width = chartColSpan(widget);
        const height = chartRowSpan(widget);
        if (!rectFits(used, widget.gridX ?? 0, widget.gridY ?? 0, width, height)) {
            overlap = true;
            break;
        }
        addOccupied(used, widget);
    }
    if (!overlap) return charts;
    return packChartsFromOrder(charts);
}

export function ensureDashboardSlots(widgets: WidgetConfig[]): WidgetConfig[] {
    const kpis = widgets.filter((widget) => widget.widgetType === 'keyMetric');
    const charts = widgets.filter((widget) => widget.widgetType !== 'keyMetric');
    const nextCharts = assignChartSlots(charts);
    if (nextCharts === charts) return widgets;
    return [...kpis, ...nextCharts];
}

export function placeNewChart(existingCharts: WidgetConfig[], widget: WidgetConfig): WidgetConfig {
    const used = new Set<string>();
    for (const chart of assignChartSlots(existingCharts)) addOccupied(used, chart);
    const width = chartColSpan(widget);
    const height = chartRowSpan(widget);
    const slot = firstFreeSlot(used, width, height, 0);
    return { ...widget, gridX: slot.gridX, gridY: slot.gridY, gridSpan: width, widgetHeight: height };
}

function bumpOverlaps(pinned: WidgetConfig, others: WidgetConfig[]): WidgetConfig[] {
    const used = new Set<string>();
    addOccupied(used, pinned);
    const sorted = [...others].sort((a, b) => {
        const row = (a.gridY ?? 0) - (b.gridY ?? 0);
        if (row !== 0) return row;
        return (a.gridX ?? 0) - (b.gridX ?? 0);
    });
    return sorted.map((widget) => {
        const width = chartColSpan(widget);
        const height = chartRowSpan(widget);
        const originX = widget.gridX ?? 0;
        const originY = widget.gridY ?? 0;
        if (rectFits(used, originX, originY, width, height)) {
            addOccupied(used, widget);
            return widget;
        }
        const slot = firstFreeSlot(used, width, height, originY);
        const shifted = { ...widget, gridX: slot.gridX, gridY: slot.gridY, gridSpan: width, widgetHeight: height };
        addOccupied(used, shifted);
        return shifted;
    });
}

function mergeDashboardWidgets(all: WidgetConfig[], nextCharts: WidgetConfig[]): WidgetConfig[] {
    const byId = new Map(nextCharts.map((widget) => [widget.id, widget]));
    return all.map((widget) => (widget.widgetType === 'keyMetric' ? widget : (byId.get(widget.id) ?? widget)));
}

export function widgetsAfterMoveChart(
    widgets: WidgetConfig[],
    widgetId: string,
    targetX: number,
    targetY: number,
): WidgetConfig[] {
    const charts = widgets.filter((widget) => widget.widgetType !== 'keyMetric');
    const slotted = assignChartSlots(charts);
    const moving = slotted.find((widget) => widget.id === widgetId);
    if (!moving) return widgets;
    const width = chartColSpan(moving);
    const height = chartRowSpan(moving);
    const dest = snapChartOrigin(targetX, targetY, width, height);
    if (dest.gridX === (moving.gridX ?? 0) && dest.gridY === (moving.gridY ?? 0)) {
        return widgets;
    }
    const pinned = { ...moving, gridX: dest.gridX, gridY: dest.gridY, gridSpan: width, widgetHeight: height };
    const others = slotted.filter((widget) => widget.id !== widgetId);
    return mergeDashboardWidgets(widgets, [pinned, ...bumpOverlaps(pinned, others)]);
}

export function widgetsAfterResizeChart(
    widgets: WidgetConfig[],
    widgetId: string,
    next: WidgetGridSize,
): WidgetConfig[] {
    const charts = widgets.filter((widget) => widget.widgetType !== 'keyMetric');
    const slotted = assignChartSlots(charts);
    const moving = slotted.find((widget) => widget.id === widgetId);
    if (!moving) return widgets;
    const origin = snapChartOrigin(
        next.gridX ?? moving.gridX ?? 0,
        next.gridY ?? moving.gridY ?? 0,
        next.gridSpan,
        next.widgetHeight,
    );
    const pinned = {
        ...moving,
        gridX: origin.gridX,
        gridY: origin.gridY,
        gridSpan: chartColSpan(next),
        widgetHeight: chartRowSpan(next),
    };
    if (
        pinned.gridX === (moving.gridX ?? 0)
        && pinned.gridY === (moving.gridY ?? 0)
        && pinned.gridSpan === chartColSpan(moving)
        && pinned.widgetHeight === chartRowSpan(moving)
    ) {
        return widgets;
    }
    const others = slotted.filter((widget) => widget.id !== widgetId);
    return mergeDashboardWidgets(widgets, [pinned, ...bumpOverlaps(pinned, others)]);
}

export function cornerCursor(corner: ResizeCorner): string {
    return corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize';
}

export function eligibleResizeCorners(
    gridX: number,
    gridY: number,
    span: number,
    height: number,
): ResizeCorner[] {
    const canGrowLeft = gridX > 0;
    const canGrowRight = gridX + span < WIDGET_SPAN_STEPS;
    const canShrinkWidth = span > 1;
    const canGrowUp = gridY > 0;
    const canGrowDown = height < WIDGET_HEIGHT_STEPS;
    const canShrinkHeight = height > 1;
    const horizontalLeft = canGrowLeft || canShrinkWidth;
    const horizontalRight = canGrowRight || canShrinkWidth;
    const verticalTop = canGrowUp || canShrinkHeight;
    const verticalBottom = canGrowDown || canShrinkHeight;
    const corners: ResizeCorner[] = [];
    if (verticalTop || horizontalLeft) corners.push('top-left');
    if (verticalTop || horizontalRight) corners.push('top-right');
    if (verticalBottom || horizontalLeft) corners.push('bottom-left');
    if (verticalBottom || horizontalRight) corners.push('bottom-right');
    return corners;
}

/** Pointer deltas are +x right / +y down. Left/top corners grow when the pointer moves outward. */
export function resizeFromCorner(
    start: { gridX: number; gridY: number; gridSpan: number; widgetHeight: number },
    corner: ResizeCorner,
    spanDelta: number,
    heightDelta: number,
): WidgetGridSize {
    const fromLeft = corner.endsWith('left');
    const fromTop = corner.startsWith('top');
    const right = start.gridX + start.gridSpan;
    const bottom = start.gridY + start.widgetHeight;

    let gridSpan = fromLeft
        ? clampGridSpan(start.gridSpan - spanDelta)
        : clampGridSpan(start.gridSpan + spanDelta);
    let gridX = fromLeft ? right - gridSpan : start.gridX;
    if (gridX < 0) {
        gridSpan = clampGridSpan(gridSpan + gridX);
        gridX = 0;
    }
    if (gridX + gridSpan > WIDGET_SPAN_STEPS) {
        gridSpan = WIDGET_SPAN_STEPS - gridX;
    }

    let widgetHeight = fromTop
        ? clampWidgetHeight(start.widgetHeight - heightDelta)
        : clampWidgetHeight(start.widgetHeight + heightDelta);
    let gridY = fromTop ? bottom - widgetHeight : start.gridY;
    if (gridY < 0) {
        widgetHeight = clampWidgetHeight(widgetHeight + gridY);
        gridY = 0;
    }

    return { gridX, gridY, gridSpan, widgetHeight };
}

export function chartGridPlacement(widget: Pick<WidgetConfig, 'gridX' | 'gridY' | 'gridSpan' | 'widgetHeight'>): {
    gridColumn: string;
    gridRow: string;
} {
    const width = chartColSpan(widget);
    const height = chartRowSpan(widget);
    const col = (widget.gridX ?? 0) + 1;
    const row = (widget.gridY ?? 0) + 1;
    return {
        gridColumn: `${col} / span ${width}`,
        gridRow: `${row} / span ${height}`,
    };
}
